import { setContext, getContext } from 'svelte';
import { get_current_component, noop } from "svelte/internal";
import { writable } from "svelte/store";
import { debounceStore, eventStore, lazyWritable } from './stores';
export function setupControl(ctor, options) {
    const svelteFile = `<${get_current_component().constructor.name}>`;
    let { name, validators = [], value, control, standalone, submitEvent, configure, register } = options;
    if (!control) {
        if (!name && !standalone)
            throw Error(`${svelteFile} please set either 'name' or 'control' property`);
        control = new ctor(name, validators, value);
    }
    else {
        // configure existing control
        if (typeof value !== "undefined" && typeof control.value === 'undefined') {
            control.setValue(value, false);
        }
        validators.forEach((_) => control.addValidator(_));
    }
    if (!(control instanceof ctor)) {
        throw new Error(`${svelteFile} 'control' needs to be of type '${ctor.name}'`);
    }
    const form = getContext("form");
    if (control instanceof FormControl) {
        if (!standalone) {
            control.submitEvent = () => form.submit();
        }
        else if (submitEvent) {
            control.submitEvent = () => submitEvent(control);
        }
        if (register) {
            setContext("form", control);
        }
    }
    if (configure)
        configure(control);
    return {
        subscribe(run) {
            const unsub = control.changes.subscribe(() => run(control));
            if (register && form && !standalone) {
                form.registerControl(control);
            }
            run(control);
            return unsubscribe;
            function unsubscribe() {
                if (register && form && !standalone) {
                    form.removeControl(control);
                }
                unsub();
            }
        },
    };
}
export class Control {
    /**
     * @constructor
     * @param name - The name of the form control
     * @param validators - (optional) some validators to validate this control against
     * @param value - (optional) some value to initialize this control
     */
    constructor(name, validators, value) {
        this._dirty = false;
        /** Emits a value everytime a control or the form changes */
        this.changes = debounceStore(this, 50);
        /** Emits a value everytime a the window should scroll to the errors of this control */
        this.scrollToErrorsChanges = eventStore();
        /** Emits a the new value everytime the value changes */
        this.valueChanges = lazyWritable();
        /** Holds a list of validation errors */
        this.errors = [];
        /** Indicates if the form is valid after validation has run */
        this.valid = false;
        this.name = name;
        this.validators = validators || [];
        this._value = value;
    }
    /** Gets the display name of this form control. defaults to control.name */
    get displayName() { return `${this._displayName || this.name}`; }
    /** Sets the display name of this form control */
    set displayName(value) {
        this._displayName = value;
        this.validate(); // ensure currently shown messages get updated
    }
    /** Hold a set of validator messages to be displayed on error */
    set validatorMessages(errors) {
        this._validatorMessages = errors;
        this.validate(); // ensure currently shown messages get updated
    }
    /** Indicates if the form is invalid after validation has run */
    get invalid() {
        // check dirty flag to prevent untouched controls to get errors styles:
        return !this.valid && this._dirty;
    }
    /** Indicates if the control has changed through user interaction */
    get dirty() {
        return this._dirty;
    }
    /** Allowes to reset the "changed through user interaction" flag of this control */
    set dirty(value) {
        this._dirty = value;
    }
    /**
     * Sets the control to dirty and triggers validation.
     * This is usefull to run validation after first userinput.
     */
    touch() {
        if (!this.dirty) {
            this._dirty = true;
            // valuebinding runs bevore event handlers
            // so it can happen to miss validation on
            // users first keystroke, because the
            // control was not set to dirty.
            this.validate();
        }
    }
    /** Gets the value of this form control */
    get value() {
        return this._value;
    }
    /** Sets the value of this form control and emits value changed events */
    set value(value) {
        this.setValue(value, true);
    }
    /** Sets the value of this form control */
    setValue(value, emitEvents, forceValidation = false) {
        if (this._value !== value) {
            const skipValueChange = !this._value && (value == null || value === 0 || (Array.isArray(value) && value.length === 0));
            this._value = value;
            if (emitEvents && !skipValueChange) {
                this.valueChanges.set(this);
            }
        }
        if (emitEvents)
            this.validate(forceValidation);
    }
    /**
     * Runs validation on this control.
     * Validation only runs on dirty controls!
     * @param force - Forces validation by setting this control and all child controls (if any) to dirty
     */
    validate(force) {
        if (force)
            this.dirty = true;
        if (this.dirty) {
            this.runValidation(force);
        }
        this.update();
        return this.valid;
    }
    /** Runs validation for this control */
    runValidation(force) {
        this.errors.splice(0);
        this.errors.push(...this.validators.map((validate) => validate(this)).filter((error) => error != null).map(_ => this.overwriteMessages(_)));
        this.valid = this.errors.length === 0;
    }
    /** Allows to overwrites default validatormessages */
    overwriteMessages(error) {
        return {
            ...error,
            message: (this._validatorMessages && this._validatorMessages[error.validator]) || error.message,
        };
    }
    /** Adds a validator to this controls validators */
    addValidator(validator) {
        if (typeof validator !== 'function')
            throw new Error(`validator needs to be a function! got '${typeof validator}'`);
        const validators = this.validators.filter((_) => 
        // compare reference equality:
        _ !== validator &&
            // find identical functions even if their reference is different, by comparing the js code:
            _.toString() !== validator.toString());
        this.validators.splice(0);
        this.validators.push(...validators, validator);
    }
    /** Notifies the control to scroll to its errors */
    scrollToErrors() {
        this.scrollToErrorsChanges.set({});
    }
    /** Notifies all consumers to update the view */
    update() {
        this.changes.set(this);
    }
}
export class FormControl extends Control {
    /**
     * @constructor
     * @param name - The name of the form control
     * @param validators - (optional) some validators to validate this control against
     * @param value - (optional) some value to initialize this control
     */
    constructor(name, validators, value) {
        super(name, validators);
        /** Holds subscribtions of controls for change detection */
        this.subscriptions = new Map();
        /** Contains all controls that are registered to this form */
        this.controls = [];
        /** Contains values that are not set to any control yet */
        this.values = {};
        /** Setting keepValues enables the form to keep values of removed controls */
        this.keepValues = true;
        /** Indicates if the control is running realtime validations */
        this.validating = false;
        /** Gets called on submit after form validation */
        this.submitEvent = noop;
        if (value !== undefined)
            this.setValue(value, false);
    }
    /** Indicates if the form has changed through user interaction */
    get dirty() {
        return super.dirty || this.controls.some((_) => _.dirty);
    }
    /** Allowes to reset the "changed through user interaction" flag of this control */
    set dirty(value) {
        super.dirty = value;
    }
    /** Gets the value of this form control */
    get value() {
        var data = {};
        if (this.keepValues) {
            data = { ...data, ...this.values };
        }
        this.controls.map((_) => (data[_.name] = _.value));
        const hasValue = Object.keys(data).length;
        return (hasValue ? data : undefined);
    }
    /** Sets the value of this form control and emits value changed events */
    set value(value) {
        super.value = value;
    }
    /** Sets the value of this form control */
    setValue(value, emitEvents, forceValidation = false) {
        const obj = value || {};
        const keys = new Set();
        for (const key of Object.keys(obj)) {
            const control = this.controls.find((_) => _.name === key);
            if (control)
                control.setValue(obj[key], emitEvents, forceValidation);
            else
                this.values[key] = obj[key];
            keys.add(key);
        }
        this.controls.filter((_) => !keys.has(_.name)).forEach((_) => (_.setValue(undefined, emitEvents, forceValidation)));
        if (emitEvents)
            this.validate(forceValidation);
    }
    /** Updates only the provided part of the value */
    patchValue(value, emitEvents = true) {
        const obj = value || {};
        for (const key of Object.keys(value)) {
            const control = this.controls.find((_) => _.name === key);
            if (control instanceof FormControl)
                control.patchValue(obj[key], emitEvents);
            else if (control)
                control.setValue(obj[key], emitEvents);
            else
                this.values[key] = obj[key];
        }
        if (emitEvents)
            this.validate();
    }
    /** Adds a control to this form */
    registerControl(control) {
        // map keepValues flag to all forms
        if (control instanceof FormControl && this.keepValues) {
            control.keepValues = true;
        }
        // map validating flag to all forms
        if (control instanceof FormControl && this.validating) {
            control.validating = true;
        }
        // restore kept value to control
        if (control.name in this.values) {
            if (this.values[control.name] !== null) {
                control.setValue(this.values[control.name], true, this.validating); // emit events because views may have been already initialized
            }
            delete this.values[control.name];
        }
        else if (this.validating) {
            control.validate(true);
        }
        // add control to form controls and subscribe to changes
        this.controls.push(control);
        this.subscriptions.set(control, merge(control.changes.subscribe(() => this.update()), control.valueChanges.subscribe(() => this.controlValueChanged(control))));
        function merge(...args) {
            return () => args.forEach(_ => _.call(null));
        }
    }
    /** Removes a control from this form */
    removeControl(control) {
        var _a;
        const index = this.controls.indexOf(control);
        if (index !== -1) {
            // dispose change subscription of control
            (_a = this.subscriptions.get(control)) === null || _a === void 0 ? void 0 : _a.call(null);
            this.subscriptions.delete(control);
            // remove control from form
            this.controls.splice(index, 1);
            // store control value to kept values, if enabled
            if (this.keepValues) {
                this.values[control.name] = control.value;
            }
        }
    }
    /** Gets called everytime a control value changes */
    controlValueChanged(control) {
        this.valueChanges.set(this);
        if (this.dirty) {
            this.validate();
        }
    }
    /** Runs validation for this form and its controls */
    runValidation(force) {
        if (force)
            this.validating = true;
        super.runValidation(force);
        let hasErrors = false;
        this.controls.forEach((control) => {
            if (control instanceof FormControl && this.validating) {
                control.validating = true;
            }
            control.validate(force);
            if (control.invalid && !hasErrors) {
                hasErrors = true;
                if (force)
                    control.scrollToErrors();
            }
        });
        this.valid = this.errors.length === 0 && !hasErrors;
    }
    /** This forces validation and triggers submit event handler */
    submit() {
        this.validate(true);
        this.submitEvent();
    }
    /** Gets a control by its name */
    get(controlName) {
        return this.controls.find((_) => _.name === controlName);
    }
}
